import { Component, ViewChild, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { merge } from "rxjs";
import { map, startWith, switchMap } from "rxjs/operators";
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from "@angular/material/table";
import { MatSort, MatSortHeader } from "@angular/material/sort";
import { timer } from "rxjs";
import { MatPaginator } from "@angular/material/paginator";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Subscription } from "rxjs";
import { ConfirmationDialog } from "../incs/confirmation-dialog.component";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { ValidationService } from "../../service/validation.service";
import { AbstractAdvisorComponent } from "../abstract/advisor.component";
import { ClientData } from "../../obj/ClientData";
import { User } from "../../entity/User";
import { State } from "../../entity/State";
import { HeaderComponent } from "../incs/header.component";
import { NgIf, DatePipe } from "@angular/common";
import { NgxMaskDirective } from "ngx-mask";

@Component({
    selector: "kilowant-client-list",
    templateUrl: "../../../html/advisor/main-client-list.html",
    imports: [HeaderComponent, FormsModule, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatSortHeader, NgIf, NgxMaskDirective, ReactiveFormsModule, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator, DatePipe]
})
export class ClientListComponent extends AbstractAdvisorComponent {
    clientsSource: MatTableDataSource<any>;

    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;
    clientListSubscription: Subscription | null;

    total: number = 0;
    currentStart: number = 0;

    currentPhoneNumber: FormControl = new FormControl("");

    countDown: Subscription;

    dialogRef: MatDialogRef<ConfirmationDialog> = null!;
    displayedColumns: string[] = ["id", "ClientName", "AccountCount", "NextAvailableDate"];

    fragment: string | null;
    states: State[] = [];
    clients: ClientData[] = [];
    // Converted cities to a signal
    private _cities = signal<string[]>([]);
    get cities(): string[] {
        return this._cities();
    }
    set cities(value: string[]) {
        this._cities.set(value);
    }
    fullVisibility: boolean[] = [];
    // Converted client to a signal
    private _client = signal<User | null>(null);
    get client(): User | null {
        return this._client();
    }
    set client(value: User | null) {
        this._client.set(value);
    }
    clientBackup: User | null = null;
    editModePos: number = -1;
    fileToUpload: File | null = null;
    deleteTaxExemptCertificateDocument: boolean;

    viewClientsType: string = "all";
    viewActiveType: string = "all";

    keywords: string | null = null;

    isCommercial: boolean = false;
    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        public validationService: ValidationService,
        dialog: MatDialog
    ) { super(router, route, dialog, authService, analyticsService, generalService); }

    ngOnInit(): void {
        this.generalService.getStates(false).then((states: any) => {
            this.states = states;
        });
        this.route.fragment.subscribe((fragment: string | null) => {
            this.fragment = fragment;
        });
    }

    ngAfterViewInit() {
        this.refreshClients();
    }

    refreshClients(): void {
        let isResidential: boolean | null = null;
        if (this.viewClientsType == "residential") {
            isResidential = true;
        }
        if (this.viewClientsType == "commercial") {
            isResidential = false;
        }
        if (this.clientListSubscription != null) {
            this.clientListSubscription.unsubscribe();
            this.clientListSubscription = null;
        }
        this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        this.clientListSubscription = merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                startWith({}),
                switchMap(() => {
                    let viewType = null;
                    if (this.viewActiveType == "active") {
                        viewType = true;
                    }
                    if (this.viewActiveType == "inactive") {
                        viewType = false;
                    }
                    return this.generalService.getClients(isResidential, viewType, this.keywords, this.total, this.paginator.pageIndex, this.paginator.pageSize, this.sort.active, (this.sort.direction == "desc"));
                }),
                map(data => {
                    if (data === null) {
                        return [];
                    }
                    if (data.key != 0 && this.total != data.key) {
                        this.total = data.key;
                    }
                    this.currentStart = this.paginator.pageIndex * this.paginator.pageSize;
                    return data.value;
                }),
            )
            .subscribe(data => { this.clients = data; this.cdr.markForCheck(); });
    }

    addClient(): void {
        this.router.navigate(["/client/add"]);
    }

    downloadReport(): void {
        var a = document.createElement("a");
        document.body.appendChild(a);
        this.generalService.getAdvisorReportUsersAccounts()
            .subscribe((data: any) => {
                var blob = new Blob([data], { type: "octet/stream" });
                var url = window.URL.createObjectURL(blob);
                a.href = url;
                a.download = "kilowant-accounts.csv";
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            });
    }

    editClient(id: number): void {
        if (this.client != null && this.client.id == id) {
            this.cancel();
            return;
        }
        this.generalService.getClient(id)
            .then(
                (res: any) => {
                    this.client = res;
                    if (this.client != null) {
                        this.isCommercial = false;
                        this.clientBackup = new User(this.client.id);
                        this.clientBackup.firstName = this.client.firstName;
                        this.clientBackup.lastName = this.client.lastName;
                        this.fileToUpload = null;
                        this.currentPhoneNumber.setValue(this.client.phone);
                        this.filterCities();
                        this.clearErrors();
                        if (this.client.company != null) {
                            this.isCommercial = true;
                        }
                    }
                    this.refreshView();
                }
            );
    }

    deleteClient(client: User): void {
        this.dialogRef = this.dialog.open(ConfirmationDialog, {
            disableClose: false
            , panelClass: "i-icon-popup"
        });
        this.dialogRef.componentInstance.confirmMessage = "Are you sure you want to delete this account?"
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.generalService.deleteClient(client.id)
                    .subscribe((res: any) => {
                        if (res) {
                            this.total = 0;
                            this.generalService.clearStoredClient();
                            this.refreshClients();
                        }
                    },
                        err => {
                            if (err.status == 400) {
                                this.setErrorText(err.error);
                            }
                        });
            }
            this.dialogRef = null!;
        });
    }

    searchSiteInputChanged(event: any): void {
        if (this.countDown) {
            this.countDown.unsubscribe();
        }
        this.countDown = timer(500).subscribe((count) => {
            this.total = 0;
            this.paginator.pageIndex = 0;
            this.refreshClients();
        });
    }

    cancel(): void {
        this.client = null;
        this.fileToUpload = null;
        this.clearErrors();
    }

    updateClient(): void {
        this.clearErrors();
        if (this.client != null) {
            if (this.client.firstName == null || this.client.firstName.length == 0) {
                this.addError("FirstName");
            }
            if (this.client.lastName == null || this.client.lastName.length == 0) {
                this.addError("LastName");
            }
            if (this.client.email == null || this.client.email.length == 0) {
                this.addError("Email");
            }
            if (this.client.addressLine1 == null || this.client.addressLine1 == "") {
                this.addError("addressLine1");
            }
            if (this.client.state == null) {
                this.addError("stateId");
            }
            if (this.client.zip == null || this.client.zip == "") {
                this.addError("zip");
            }
            if (this.client.city == null || this.client.city == "") {
                this.addError("city");
            }
            if (this.client.company != null && (this.client.company.name == null || this.client.company.name == "")) {
                this.addError("Company");
            }
            if (this.client.company != null && this.client.company.taxExempt == true && (this.client.company.federalTaxID == null || this.client.company.federalTaxID.length == 0)) {
                this.addError("FederalTaxID");
            }
            if (this.client.company != null && this.client.company.taxExempt == true && this.fileToUpload == null && this.client.company.taxExemptCertificateDocument == null) {
                this.addError("fileToUpload");
            }
            if (this.errorY().showError()) {
                this.setErrorText("Please enter the data requested above.");
                return;
            }

            if (!this.currentPhoneNumber.valid) {
                this.addError("Phone");
                this.setErrorText("Phone number is not correct.");
                return;
            }
            this.client.phone = this.currentPhoneNumber.value;

            this.generalService.updateUser(this.client, this.fileToUpload, this.deleteTaxExemptCertificateDocument, true).subscribe(
                (res: any) => {
                    if (res) {
                        if (!this.client?.isActive) {
                            this.generalService.clearStoredClient();
                        }
                        this.client = null;
                        this.fileToUpload = null;
                        this.clearErrors();
                        this.refreshClients();
                    }
                },
                (err) => {
                    if (err.status == 400) {
                        this.setErrorText(err.error);
                    }
                }
            );
        }
    }

    changeClientView(): void {
        this.total = 0;
        this.refreshClients();
    }

    filterCities(): void {
        if (this.client != null) {
            this.cities = [];
            if (this.client.state == null) {
                return;
            }
            this.generalService.getCities(this.client.state).then((cities) => {
                this.cities = cities; 
            });
        }
    }

    downloadFile(ifExisting: boolean, id: number | null) {
        if (!ifExisting) {
            this.generalService.sendFileToClient(this.fileToUpload);
        } else if (id != null) {
            this.generalService.sendFileFromServerToClient(id);
        }
    }

    deleteFile() {
        this.fileToUpload = null;
    }

    handleFileInput(event: Event) {
        const target = event.target as HTMLInputElement;
        this.fileToUpload = (target.files as FileList).item(0) as File;
        this.resetError("fileToUpload");
    }

    deleteExistingFile(key: number) {
        this.deleteTaxExemptCertificateDocument = true;
        if (this.client != null && this.client.company != null) {
            this.client.company.taxExemptCertificateDocument = null!;
        }
    }

    ngOnDestroy() {
        if (this.clientListSubscription != null) {
            this.clientListSubscription.unsubscribe();
        }
        if (this.countDown != null) {
            this.countDown.unsubscribe();
        }
    }
}